import React from 'react';

import { FormControl, FormHelperText, InputLabel, MenuItem, Paper, Select, Typography, IconButton } from '@material-ui/core';
import { Delete, ArrowUpward, ArrowDownward } from '@material-ui/icons';
import { FieldArray } from 'formik';
import type { FormikProps } from 'formik';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';

import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import FormRichTextInput from 'components/FormRichTextInput';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import action_library_messages from 'messages/action_library_messages';

import type { ActionTemplateFormValues, ChecklistStep } from '../../ActionTemplateForm.types';

import useStyles from './ActionTemplateSteps.styles';

type ActionTemplateStepsProps = {
  formik: FormikProps<ActionTemplateFormValues>;
};

const ActionTemplateSteps: React.FC<ActionTemplateStepsProps> = ({ formik }) => {
  const { values, errors, touched, setFieldValue } = formik;
  const { t } = useTranslation();
  const styles = useStyles();

  // Check if checklist has validation error
  const checklistError = errors.checklist;
  const checklistTouched = touched.checklist;
  const showChecklistError = checklistTouched && Boolean(checklistError) && values.checklist.length === 0;

  // Helper to convert boolean to semantic string for select component
  const getMandatoryValue = (value: boolean | string | undefined): string => {
    if (typeof value === 'boolean') {
      return value ? 'mandatory' : 'optional';
    }
    if (typeof value === 'string') {
      return value;
    }
    return 'mandatory'; // default
  };

  // Helper to handle select change and convert semantic string back to boolean
  const handleMandatoryChange = (index: number) => (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const stringValue = event.target.value as string;
    const booleanValue = stringValue === 'mandatory';
    setFieldValue(`checklist.${index}.isMandatory`, booleanValue);
  };

  return (
    <FieldArray name='checklist'>
      {({ remove, move }) => (
        <div>
          {values.checklist.map((step: ChecklistStep, index: number) => (
            <Paper key={step.id} data-step-id={step.id} style={{ padding: 16, marginBottom: 16 }}>
              <Typography variant='h6'>{t(action_library_messages.steps.step_title, { number: index + 1 })}</Typography>
              <CenteredGrid>
                <FormTextInput
                  className={styles.marginBottom}
                  formik={formik}
                  id={`checklist.${index}.title`}
                  label={t(action_library_messages.steps.title)}
                  required
                />
                <FormRichTextInput
                  className={styles.richTextMarginBottom}
                  formik={formik}
                  id={`checklist.${index}.instruction`}
                  label={t(action_library_messages.steps.instruction)}
                  required
                />
                <FormControl
                  className={styles.marginBottom}
                  error={get(touched, `checklist.${index}.isMandatory`) && Boolean(get(errors, `checklist.${index}.isMandatory`))}
                  variant='outlined'
                >
                  <InputLabel id={`checklist.${index}.isMandatory-label`}>{t(action_library_messages.steps.mandatory)}</InputLabel>
                  <Select
                    id={`checklist.${index}.isMandatory`}
                    label={t(action_library_messages.steps.mandatory)}
                    labelId={`checklist.${index}.isMandatory-label`}
                    onBlur={() => formik.setFieldTouched(`checklist.${index}.isMandatory`, true)}
                    onChange={handleMandatoryChange(index)}
                    value={getMandatoryValue(get(values, `checklist.${index}.isMandatory`))}
                  >
                    <MenuItem value='mandatory'>{t(action_library_messages.steps.mandatory_yes)}</MenuItem>
                    <MenuItem value='optional'>{t(action_library_messages.steps.mandatory_no)}</MenuItem>
                  </Select>
                  {get(touched, `checklist.${index}.isMandatory`) && get(errors, `checklist.${index}.isMandatory`) && (
                    <FormHelperText error>{get(errors, `checklist.${index}.isMandatory`)}</FormHelperText>
                  )}
                </FormControl>
                <FormTextInput
                  className={styles.marginBottom}
                  formik={formik}
                  id={`checklist.${index}.relatedReading`}
                  label={t(action_library_messages.steps.related_reading)}
                />
                <FormTextInput
                  formik={formik}
                  id={`checklist.${index}.relevantFor`}
                  label={t(action_library_messages.steps.relevant_for)}
                />
              </CenteredGrid>
              <IconButton disabled={index === 0} onClick={() => move(index, index - 1)}>
                <ArrowUpward />
              </IconButton>
              <IconButton disabled={index === values.checklist.length - 1} onClick={() => move(index, index + 1)}>
                <ArrowDownward />
              </IconButton>
              <IconButton onClick={() => remove(index)}>
                <Delete />
              </IconButton>
            </Paper>
          ))}
          {showChecklistError && (
            <FormHelperText error style={{ marginBottom: 16 }}>
              {typeof checklistError === 'string' ? checklistError : t(action_library_messages.steps.at_least_one_required)}
            </FormHelperText>
          )}
        </div>
      )}
    </FieldArray>
  );
};

export default ActionTemplateSteps;
